"use client"
import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useLanguage } from "../contexts/LanguageContext"
import { useApartmentById } from "../contexts/aparts/useApartments"
import { MapPin } from "lucide-react"
import AvailabilityCalendar from "./AvailabilityCalendar"
import "../styles/ApartmentDetails.css"

// Separate component for the availability calendar section
const ApartmentAvailability = ({ apartmentId }) => {
  // comingSoon - שורה 1
  const { t } = useLanguage();
  
  return (
    <section className="availability-section">
      <div className="container">
        // comingSoon - 6 שורות
        <div className="coming-soon-overlay">
          <div className="coming-soon-content">
            <h2 className="coming-soon-title">{t("comingSoon")}</h2>
            <p className="coming-soon-message">{t("comingSoonMessage")}</p>
          </div>
        </div>
        <AvailabilityCalendar apartmentId={apartmentId} />
      </div>
    </section>
  )
}

const ApartmentDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t, language } = useLanguage()
  const [activeImage, setActiveImage] = useState(0)
  const [loading, setLoading] = useState(true)
  
  // Get apartment data from context
  const apartment = useApartmentById(id)

  // Define all variables at the top level, before any conditional returns
  const apartmentTitle = apartment ? (apartment[`title${language.charAt(0).toUpperCase() + language.slice(1)}`] || apartment.titleEn || "") : ""
  const apartmentDescription = apartment ? (apartment[`description${language.charAt(0).toUpperCase() + language.slice(1)}`] || apartment.descriptionEn || "") : ""
  const apartmentAddress = apartment?.location ? (apartment.location[`address${language.charAt(0).toUpperCase() + language.slice(1)}`] || apartment.location.addressEn || "") : ""

  // מעבר לתמונה הבאה
  const nextImage = () => {
    if (apartment && apartment.images && apartment.images.length > 0) {
      setActiveImage((prev) => (prev + 1) % apartment.images.length)
    }
  }

  // מעבר לתמונה הקודמת
  const prevImage = () => {
    if (apartment && apartment.images && apartment.images.length > 0) {
      setActiveImage((prev) => (prev - 1 + apartment.images.length) % apartment.images.length)
    }
  }

  // בחירת תמונה ספציפית
  const selectImage = (index) => {
    if (apartment && apartment.images && apartment.images.length > index) {
      setActiveImage(index)
    }
  }

  // פונקציה לשליחת הודעת WhatsApp בלחיצה על כפתור Book Now
  const handleBookNowClick = () => {
    if (!apartment) return
    
    const phoneNumber = "972584204297" // מספר הטלפון מהWhatsAppButton
    const message = `Hello, I would like to book the room ${apartment.titleEn || ''} ${apartment.internalCode || ''}`
    const encodedMessage = encodeURIComponent(message)
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, "_blank")
  }

  // פונקציה לשליחת הודעת WhatsApp בלחיצה על כפתור Contact Us
  const handleContactUsClick = () => {
    if (!apartment) return
    
    const phoneNumber = "972584204297" // מספר הטלפון מהWhatsAppButton
    const message = `Hello, I would like to get more information about ${apartment.titleEn || ''} ${apartment.internalCode || ''}`
    const encodedMessage = encodeURIComponent(message)
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, "_blank")
  }

  // Loading effect - must be defined before any conditional returns
  useEffect(() => {
    if (apartment) {
      setTimeout(() => {
        setLoading(false)
      }, 500)
    } else {
      // If apartment not found, redirect to home page
      navigate('/')
    }
  }, [apartment, id, navigate])

  // Scroll to top when component mounts or when id changes
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [id])

  // עדכון מטא-תגים באופן דינמי - must be defined before any conditional returns
  useEffect(() => {
    if (apartment) {
      // עדכון כותרת הדף
      document.title = `${apartmentTitle} | Gold House - דירות יוקרה להשכרה בירושלים`;
      
      // יצירת מטא-תגים דינמיים
      const metaTags = [
        { name: "description", content: `${apartmentDescription} - דירת יוקרה להשכרה לטווח קצר בירושלים. ${apartment.bedrooms} חדרי שינה, ${apartment.size} מ"ר, מתאים ל-${apartment.guests} אורחים.` },
        { name: "keywords", content: `דירות יוקרה בירושלים, השכרה לטווח קצר, דירות להשכרה בירושלים, דירות נופש, מלון בוטיק בירושלים, ${apartmentTitle}, ${apartmentAddress}, דירות מאובזרות, דירות לזוגות, דירות למשפחות` },
        { property: "og:title", content: `${apartmentTitle} | Gold House - דירות יוקרה להשכרה בירושלים` },
        { property: "og:description", content: `${apartmentDescription} - דירת יוקרה להשכרה לטווח קצר בירושלים. ${apartment.bedrooms} חדרי שינה, ${apartment.size} מ"ר, מתאים ל-${apartment.guests} אורחים.` },
        { property: "og:image", content: apartment.images[0] },
        { property: "og:url", content: `https://goldhouse-apart.com/apartment/${id}` },
        { property: "og:type", content: "website" }
      ];
      
      // הסרת מטא-תגים קיימים
      const existingMetaTags = document.querySelectorAll('meta[name="description"], meta[name="keywords"], meta[property^="og:"]');
      existingMetaTags.forEach(tag => tag.remove());
      
      // הוספת מטא-תגים חדשים
      metaTags.forEach(tagInfo => {
        const tag = document.createElement('meta');
        const attributeName = tagInfo.property ? 'property' : 'name';
        tag.setAttribute(attributeName, tagInfo[attributeName]);
        tag.setAttribute('content', tagInfo.content);
        document.head.appendChild(tag);
      });
      
      // הוספת קישור קנוני
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', `https://goldhouse-apart.com/apartment/${id}`);
      
      // הוספת structured data
      const structuredDataScript = document.createElement('script');
      structuredDataScript.type = 'application/ld+json';
      structuredDataScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Apartment",
        "name": apartmentTitle,
        "description": apartmentDescription,
        "image": apartment.images,
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "ירושלים",
          "addressRegion": "ירושלים",
          "addressCountry": "IL"
        },
        "numberOfRooms": apartment.bedrooms,
        "floorSize": {
          "@type": "QuantitativeValue",
          "value": apartment.size,
          "unitCode": "MTK"
        },
        "amenityFeature": apartment.amenities?.map(amenity => ({
          "@type": "LocationFeatureSpecification",
          "name": typeof amenity === 'object' ? (amenity[`name${language === 'en' ? '' : language === 'es' ? 'Es' : language === 'fr' ? 'Fr' : language === 'he' ? 'He' : ''}`] || amenity.name) : amenity,
          "value": true
        })),
        "url": `https://goldhouse-apart.com/apartment/${id}`
      });
      
      // הסרת structured data קיים
      const existingStructuredData = document.querySelector('script[type="application/ld+json"]');
      if (existingStructuredData) {
        existingStructuredData.remove();
      }
      
      document.head.appendChild(structuredDataScript);
    }
    
    // ניקוי בעת עזיבת הדף
    return () => {
      const existingMetaTags = document.querySelectorAll('meta[name="description"], meta[name="keywords"], meta[property^="og:"]');
      existingMetaTags.forEach(tag => tag.remove());
      
      const existingStructuredData = document.querySelector('script[type="application/ld+json"]');
      if (existingStructuredData) {
        existingStructuredData.remove();
      }
    };
  }, [apartment, apartmentTitle, apartmentDescription, apartmentAddress, id, language]);
  
  // Conditional returns must come after all hooks
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{t("loading")}</p>
      </div>
    )
  }

  if (!apartment) {
    return null
  }

  return (
    <div className="apartment-page">
      <div className="header-spacer"></div>
      <section className="apartment-details">
        <div className="container">
          <button className="back-button" onClick={() => navigate('/')}>
            {t("backToApartments")}
          </button>
          
          <div className="apartment-details-content">
            <div className="image-gallery">
              <div className="main-image">
                <button className="gallery-nav prev" onClick={prevImage}>❮</button>
                <img src={apartment.images[activeImage]} alt={apartment.title} />
                <button className="gallery-nav next" onClick={nextImage}>❯</button>
              </div>
              <div className="thumbnail-container">
                {apartment.images.map((image, index) => (
                  <div 
                    key={index} 
                    className={`thumbnail ${activeImage === index ? 'active' : ''}`}
                    onClick={() => selectImage(index)}
                  >
                    <img src={image} alt={`Thumbnail ${index + 1}`} />
                  </div>
                ))}
              </div>
            </div>
            
            <div className="apartment-info">
              <h1 className="apartment-title">{apartment[`title${language.charAt(0).toUpperCase() + language.slice(1)}`] || apartment.titleEn || ""}</h1>
              <div className="apartment-description-short">{apartment[`description${language.charAt(0).toUpperCase() + language.slice(1)}`] || apartment.descriptionEn || ""}</div>
              <div className="apartment-location">
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(apartment.location?.[`address${language.charAt(0).toUpperCase() + language.slice(1)}`] || apartment.location?.addressEn || "")}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="location-address gold-address"
                >
                  <MapPin size={18} className="location-icon" style={{ marginRight: '8px' }}/>
                  <strong>{apartment.location?.[`address${language.charAt(0).toUpperCase() + language.slice(1)}`] || apartment.location?.addressEn || ""}</strong>
                </a>
              </div>
              
              <div className="apartment-key-details">
                <div className="detail-item">
                  <span className="detail-icon">🛏️</span>
                  <span className="detail-text">{apartment.bedrooms} {t("bedrooms")}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-icon">🚿</span>
                  <span className="detail-text">{apartment.bathrooms} {t("bathrooms")}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-icon">👥</span>
                  <span className="detail-text">{apartment.guests} {t("guestsCount")}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-icon">📏</span>
                  <span className="detail-text">{apartment.size} {t("sqm")}</span>
                </div>
              </div>              
              <div className="apartment-description">
                <h2>{t("description")}</h2>
                <p>{apartment[`longDescription${language.charAt(0).toUpperCase() + language.slice(1)}`] || apartment.longDescriptionEn || ""}</p>
              </div>
              
              <div className="apartment-amenities">
                <h2>{t("amenities")}</h2>
                <ul className="amenities-list">
                  {apartment.amenities && apartment.amenities.map((amenity, index) => (
                    <li key={index} className="amenity-item">
                      <span className="amenity-icon">✓</span>
                      <span>
                        {typeof amenity === 'object' && amenity !== null
                          ? amenity[`name${language === 'en' ? '' : language === 'es' ? 'Es' : language === 'fr' ? 'Fr' : language === 'he' ? 'He' : ''}`] || amenity.name
                          : amenity}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="booking-section">
                <button className="booking-button" onClick={handleBookNowClick}>{t("bookNow")}</button>
                <button className="contact-button" onClick={handleContactUsClick}>{t("contactUs")}</button>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Availability Calendar Section */}
      <ApartmentAvailability apartmentId={id} />
      
      {/* Map Section */}
      <section className="map-section">
        <div className="container">
          <h2 className="map-title">{t("locationTitle")}</h2>
          <div className="map-container">
            <div className="map-address">
              <MapPin size={24} className="map-pin-icon" />
              <p>{apartment.location?.[`address${language.charAt(0).toUpperCase() + language.slice(1)}`] || apartment.location?.addressEn || ""}</p>
            </div>
            <div className="map-frame">
              <iframe
                title="Apartment Location"
                width="100%"
                height="450"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(
                  apartment.location?.[`address${language.charAt(0).toUpperCase() + language.slice(1)}`] || 
                  apartment.location?.addressEn || 
                  ""
                )}`}
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ApartmentDetails
